import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useSupportData } from './SupportOverview';
import SupportInstanceSelector from '../../components/SupportInstanceSelector';
import { useSupportInstanceSelection } from '../../context/SupportInstanceSelectionContext';
import NoDataPlaceholder from '../../components/NoDataPlaceholder';
import SlidingAiPanel from '../../components/SlidingAiPanel';
import { getSupportStaffAiSummary, getSupportTeamAiSummary } from '../../api';
import { StaffPerformance } from '../../types/SupportTypes';
import '../development/DevelopmentDevelopers.css';
import SupportMemberTrumpCard from '../../components/SupportMemberTrumpCard';
import { sortMembersByPerformance } from '../../utils/supportPerformanceUtils';
import { format } from 'date-fns';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  RadialLinearScale,
  Filler
} from 'chart.js';
import { Line, Bar, Radar } from 'react-chartjs-2';
import SupportMemberDetail from '../../components/support/SupportMemberDetail';
import SupportTeamDetail from '../../components/support/SupportTeamDetail';
import LoadingSpinner from '../../components/LoadingSpinner';
import { ChevronRight } from '@mui/icons-material';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend,
  Filler
);

const SupportTeamMembers: React.FC = () => {
  const { data, instances, loading, error } = useSupportData();
  const { selectedSupportCustomerIntegrationId, setSelectedSupportCustomerIntegrationId } = useSupportInstanceSelection();
  const location = useLocation();
  const [selectedMember, setSelectedMember] = useState<StaffPerformance | null>(null);
  const [activeCardIndex, setActiveCardIndex] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [aiSummary, setAiSummary] = useState<{ summary: string | null; createdOn: string | null } | null>(null);
  const [floatingCard, setFloatingCard] = useState<{
    idx: number;
    top: number;
    left: number;
    width: number;
    height: number;
    animating: boolean;
    containerTop?: number;
  } | null>(null);

  const selectedCardWindowYPosition = 0.6;
  const cardDomRefs = useRef<Array<HTMLDivElement | null>>([]);

  // Responsive state (mirrors DevelopmentDevelopers)
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  // Handle responsive breakpoints
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1024);
      
      // Close mobile drawer when switching to desktop
      if (width >= 1024) {
        setMobileDrawerOpen(false);
      }
    };

    handleResize(); // Set initial state
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle body scroll prevention when mobile drawer is open
  useEffect(() => {
    if (isMobile) {
      if (mobileDrawerOpen) {
        // Store current scroll position
        const scrollY = window.scrollY;
        document.body.style.top = `-${scrollY}px`;
        document.body.classList.add('mobile-drawer-open');
      } else {
        // Restore scroll position
        const scrollY = document.body.style.top;
        document.body.classList.remove('mobile-drawer-open');
        document.body.style.top = '';
        if (scrollY) {
          window.scrollTo(0, parseInt(scrollY || '0') * -1);
        }
      }
    }
    
    // Cleanup on unmount
    return () => {
      document.body.classList.remove('mobile-drawer-open');
      document.body.style.top = '';
    };
  }, [mobileDrawerOpen, isMobile]);

  // FLIP Animation Handlers
  const handleCardClick = (
    e: React.MouseEvent<HTMLDivElement>,
    idx: number,
    member: StaffPerformance
  ) => {
    if (floatingCard) return;
    if (activeCardIndex === idx) {
      setActiveCardIndex(idx);
      setSelectedMember(member);
      return;
    }

    // Prefer measuring the actual clicked node; fallback to stored ref
    let cardNode = cardDomRefs.current[idx] || (e.currentTarget as HTMLDivElement);
    let rect = cardNode?.getBoundingClientRect();
    if (cardNode && rect?.height === 0) {
      cardNode = e.currentTarget as HTMLDivElement;
      rect = cardNode?.getBoundingClientRect();
    }

    if (!cardNode || !rect) {
      setActiveCardIndex(idx);
      setSelectedMember(member);
      return;
    }

    if (isMobile) {
      // Center card in viewport on mobile
      const containerTopOffset = (window.innerHeight * 0.5) - (rect.height * 0.5) - 15;
      const targetLeft = (window.innerWidth * 0.5) - (rect.width * 0.5) - 11;

      setFloatingCard({
        idx,
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
        animating: false,
        containerTop: rect.top,
      });

      requestAnimationFrame(() => {
        setFloatingCard(fc => fc && {
          ...fc,
          top: containerTopOffset,
          left: targetLeft,
          width: rect!.width,
          height: rect!.height,
          animating: true,
          containerTop: containerTopOffset,
        });
      });
    } else {
      // Desktop animation (unchanged positioning logic)
      const containerTopOffset = (window.innerHeight * selectedCardWindowYPosition) - rect.height + 65;
      const targetLeft = rect.left + 73;

      setFloatingCard({
        idx,
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
        animating: false,
        containerTop: rect.top,
      });

      requestAnimationFrame(() => {
        setFloatingCard(fc => fc && {
          ...fc,
          top: containerTopOffset,
          left: targetLeft,
          width: rect!.width,
          height: rect!.height,
          animating: true,
          containerTop: containerTopOffset,
        });
      });
    }
  };

  // Calculate overall scores and sort members by performance using the utility function
  const membersWithScores = useMemo(() => {
    if (!data || !data.staffMonthlyTrendStats) return [];
    return sortMembersByPerformance(data.staffMonthlyTrendStats, undefined, data.staffPerformance);
  }, [data]);

  // Filter members based on search query - must be called before any conditional returns
  const searchFilteredMembers = useMemo(() => {
    if (!searchQuery.trim()) return membersWithScores;

    const query = searchQuery.toLowerCase().trim();
    return membersWithScores.filter(member =>
      member.name.toLowerCase().includes(query)
    );
  }, [membersWithScores, searchQuery]);

  // Handle navigation state - set selectedMember if passed from navigation
  useEffect(() => {
    if(data && membersWithScores && membersWithScores.length > 0) {
      const state = location.state as { selectedMember?: string } | null;
      if (state?.selectedMember && membersWithScores.length > 0) {
        // Find the member by name
        const member = membersWithScores.find(m => m.name === state.selectedMember);
        if (member) {
          const memberIndex = searchFilteredMembers.findIndex(m => m.name === state.selectedMember);
          if (memberIndex !== -1) {
            setSelectedMember(member);
            setActiveCardIndex(memberIndex);
          }
        }
        // Clear the state to prevent re-selection on subsequent renders
        window.history.replaceState({}, document.title);
      }
    }
  }, [location.state, membersWithScores, searchFilteredMembers]);


  const handleFloatingCardTransitionEnd = () => {
    if (floatingCard) {
      setActiveCardIndex(floatingCard.idx);
      const member = searchFilteredMembers[floatingCard.idx];
      setSelectedMember(member);
      // Update AI summary when member is selected
      if (member && data && data.aiStaffSummary) {
        //setAiSummary(data.aiStaffSummary?.AISummary);
      }
      setFloatingCard(null);
    }
  };

  // Update AI summary when a member is selected or deselected
  useEffect(() => {
    if (data) {
      if (selectedMember) {
        // Fetch the staff member's AI summary when a member is selected
        fetchFullStaffSummary();
      } else {
        // Use team AI summary from the dashboard response
        setAiSummary(data.aiStaffSummary?.summary ? { 
          summary: data.aiStaffSummary.summary, 
          createdOn: data.aiStaffSummary.createdOn 
        } : null);
      }
    }
  }, [selectedMember, data]);

  // Callback to update AI summary when streaming content changes
  const updateAiSummary = (summary: string | null, createdOn: string | null) => {
    setAiSummary({ summary, createdOn });
  };

  // Function to fetch staff AI summary for generateInitialAi (returns just the content)
  const fetchStaffSummary = async () => {
    try {
      if (selectedMember && data) {
        const staffSummaryData = await getSupportStaffAiSummary(selectedMember.name, data.summaryId);
        return { summary: staffSummaryData?.aiSummary || '', createdOn: staffSummaryData?.createdOn || '' };
      }
      return { summary: '', createdOn: '' };
    } catch (error) {
      console.error('Error fetching staff AI summary:', error);
      return { summary: 'Failed to load staff insights. Please try again later.', createdOn: '' };
    }
  };

  // Function to fetch full staff AI summary data (including creation date)
  const fetchFullStaffSummary = async () => {
    try {
      if (selectedMember && data) {
        const staffSummaryData = await getSupportStaffAiSummary(selectedMember.name, data.summaryId);
        if (staffSummaryData) {
          setAiSummary({
            summary: staffSummaryData.aiSummary,
            createdOn: staffSummaryData.createdOn
          });
        } else {
          setAiSummary(null);
        }
      }
    } catch (error) {
      console.error('Error fetching staff AI summary:', error);
      setAiSummary(null);
    }
  };

  if (loading) return <LoadingSpinner title="Loading support team data..." />;
  if (error) return <div>Error: {error}</div>;
  if (!data || !data.summaryId) {
    return (
      <NoDataPlaceholder
        title="No Team Member Data Available"
        message={
          <>
            <p>To view team member performance metrics, ensure an integration is set up in the Settings page.</p>
            <p>Once integrated, data should appear here within 10 minutes.</p>
          </>
        }
      />
    );
  }

  const cardDeckTopOffset = 20;

  const dataSnapshotRange = `${format(new Date(data.snapshotStartDate), 'do MMMM yyyy HH:mm')} 
  - ${format(new Date(data.summaryDate), 'do MMMM yyyy HH:mm')}`;

  return (
    <div style={{ padding: isMobile ? '0' : '1rem 2rem 2rem 2rem' }}>
      {/* Mobile fixed top instance selector */}
      {isMobile && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 999,
            paddingTop: 40,
            background: '#ffffff',
            borderBottom: '1px solid #e5e7eb',
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
          }}
        >
          <div style={{ maxWidth: 1400, margin: '0 auto', padding: '12px 16px' }}>
            <div style={{
              fontSize: 20,
              fontWeight: 300,
              color: '#374151',
              marginBottom: 8,
              textAlign: 'center',
              fontFamily: 'Raleway, sans-serif'
            }}>
              Support Team Members
            </div>
            {instances && instances.length > 0 && (
              <select
                value={selectedSupportCustomerIntegrationId || ''}
                onChange={(e) => setSelectedSupportCustomerIntegrationId(Number(e.target.value))}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: 8,
                  border: '1px solid #d1d5db',
                  background: '#f9fafb',
                  fontSize: 14,
                  color: '#111827'
                }}
              >
                {instances.map((instance) => (
                  <option key={instance.customerIntegrationId} value={instance.customerIntegrationId}>
                    {instance.instanceName}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>
      )}
      {/* Spacer to offset fixed header height on mobile */}
      {isMobile && <div style={{ height: 70 }} />}

      {/* Desktop header - hidden on mobile */}
      {!isMobile && (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '1rem',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <h1 style={{
            fontSize: '2rem',
            lineHeight: '1.2',
            fontFamily: 'Raleway, sans-serif',
            fontWeight: 300,
            margin: 0,
            marginLeft: '1rem'
          }}>
            {data?.instanceName ? `${data.instanceName} - Team Members` : 'Support Team Performance'}
          </h1>
          
          {instances.length > 0 && (
            <SupportInstanceSelector
              instances={instances}
              selectedCustomerIntegrationId={selectedSupportCustomerIntegrationId}
              onSelectInstance={setSelectedSupportCustomerIntegrationId}
            />
          )}
        </div>
      )}

      {data?.summaryDate && (
        <p className="dashboard-subtitle" style={{ marginBottom: isMobile ? '0.5rem' : '2rem', color: '#666', fontSize: '0.9rem', paddingLeft: isMobile ? '2rem' : undefined, paddingRight: isMobile ? '2rem' : undefined }}>
          Data snapshot: {dataSnapshotRange}
        </p>
      )}



      {/* Main Content */}
      <div className={`developers-layout-container ${isMobile ? 'mobile' : isTablet ? 'tablet' : ''}`}>
        {/* Mobile: Active and Floating card layers rendered globally above the drawer */}
        {isMobile && !floatingCard && activeCardIndex !== null && (
          <div
            className={`deck-card-wrapper ${mobileDrawerOpen ? 'mobile-deck-card-active' : 'mobile-deck-card-active-hidden'}`}
            style={{ zIndex: 10000, left:10 }}
            ref={el => { if (activeCardIndex !== null) cardDomRefs.current[activeCardIndex] = el; }}
          >
            <SupportMemberTrumpCard
              member={searchFilteredMembers[activeCardIndex]}
              monthlyTrendStats={data.staffMonthlyTrendStats}
              active={true}
              onClick={() => { }}
              onCloseActive={() => {
                setActiveCardIndex(null);
                setSelectedMember(null);
              }}
              isMobile={true}
              onMobileDataClick={() => {
                setMobileDrawerOpen(false);
              }}
            />
          </div>
        )}

        {isMobile && floatingCard && (
          <div
            className="deck-card-wrapper mobile-floating-card"
            style={{
              position: 'fixed',
              zIndex: 10000,
              top: floatingCard.top,
              left: floatingCard.left,
              width: floatingCard.width,
              height: floatingCard.height,
              transition: floatingCard.animating ? 'all 0.5s cubic-bezier(.4,1.3,.6,1)' : 'none',
              transform: floatingCard.animating ? 'scale(1.08) translateY(0)' : 'none',
              pointerEvents: 'none'
            }}
            onTransitionEnd={handleFloatingCardTransitionEnd}
          >
            <SupportMemberTrumpCard
              member={searchFilteredMembers[floatingCard.idx]}
              monthlyTrendStats={data.staffMonthlyTrendStats}
              active={true}
              onClick={() => { }}
              onCloseActive={() => { }}
              isMobile={true}
              onMobileDataClick={() => {
                setMobileDrawerOpen(false);
              }}
            />
          </div>
        )}

        {/* Mobile drawer for cards */}
        {isMobile && (
          <>
            {/* Mobile drawer overlay */}
            {mobileDrawerOpen && (
              <div 
                className="mobile-drawer-overlay"
                onClick={() => setMobileDrawerOpen(false)}
              />
            )}
            
            {/* Mobile drawer - replicating desktop card stack */}
            <div className={`mobile-cards-drawer ${mobileDrawerOpen ? 'open' : ''}`}>
              {/* Search */}
              <div style={{ marginBottom: '0.5rem', position: 'relative', zIndex: 2000 }}>
                <input
                  type="text"
                  placeholder="Search team members..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '4px',
                    border: '1px solid #ccc',
                    width: '250px',
                    fontSize: '14px'
                  }}
                />
              </div>

              {/* Mobile Team Member Cards - Stacked like desktop */}
              <div className="mobile-deck-of-cards-container">
                {searchFilteredMembers.length > 0 ? (
                  searchFilteredMembers.map((member, idx) => (
                    activeCardIndex !== idx ? (
                      <div
                        key={member.name}
                        className="mobile-deck-card-wrapper"
                        style={{
                          zIndex: 1100 + idx,
                          top: `${(idx * 48) + cardDeckTopOffset}px`,
                          visibility: floatingCard && floatingCard.idx === idx ? 'hidden' : undefined,
                        }}
                        ref={el => { cardDomRefs.current[idx] = el; }}
                        onClick={e => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleCardClick(e, idx, member);
                        }}
                      >
                        <SupportMemberTrumpCard
                          member={member}
                          monthlyTrendStats={data.staffMonthlyTrendStats}
                          active={false}
                          onClick={() => {}}
                          onCloseActive={() => {
                            setActiveCardIndex(null);
                            setSelectedMember(null);
                          }}
                          isMobile={false}
                        />
                      </div>
                    ) : <React.Fragment key={member.name} />
                  ))
                ) : searchQuery && data.staffPerformance.length > 0 ? (
                  <p style={{ position: 'relative', zIndex: 2000, padding: '20px', color: '#666' }}>No team members found matching "{searchQuery}".</p>
                ) : (
                  <p style={{ position: 'relative', zIndex: 2000, padding: '20px', color: '#666' }}>No team member data available.</p>
                )}
              </div>
            </div>
            
            {/* Mobile drawer handle */}
            <div 
              className="mobile-drawer-handle"
              onClick={() => setMobileDrawerOpen(!mobileDrawerOpen)}
            >
              <div className="handle-icon">
                <ChevronRight />
              </div>
              <span className="handle-text">Choose a Team Member</span>
            </div>
          </>
        )}
        
        {/* Team Member Cards - Desktop container */}
        <div className={`deck-of-cards-container-wrapper ${isMobile ? 'mobile-hidden' : ''}`}>
          {/* Search */}
          <div style={{ marginBottom: '1.5rem' }}>
            <input
              type="text"
              placeholder="Search team members..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                padding: '8px 12px',
                borderRadius: '4px',
                border: '1px solid #ccc',
                width: '250px',
                fontSize: '14px'
              }}
            />
          </div>
          {/* <div className="deck-of-cards-title">
            <h3>Recent Performance</h3>
          </div> */}

          {/* Active card - moved to parent container */}
          {!isMobile && !floatingCard && activeCardIndex !== null && (
            <div
              className="deck-card-wrapper deck-card-active"
              style={{
                zIndex: 1000 + activeCardIndex,
                position: 'absolute',
                top: `${(window.innerHeight * selectedCardWindowYPosition) - (cardDomRefs.current[activeCardIndex] ? cardDomRefs.current[activeCardIndex]!.offsetHeight : 0) + 54}px`,
                transition: 'none',
              }}
              ref={el => { if (activeCardIndex !== null) cardDomRefs.current[activeCardIndex] = el; }}
              onClick={e => {
                if (activeCardIndex !== null) {
                  handleCardClick(e, activeCardIndex, searchFilteredMembers[activeCardIndex]);
                }
              }}
            >
              <SupportMemberTrumpCard
                member={searchFilteredMembers[activeCardIndex]}
                monthlyTrendStats={data.staffMonthlyTrendStats}
                active={true}
                onClick={() => { }}
                onCloseActive={() => {
                  setActiveCardIndex(null);
                  setSelectedMember(null);
                }}
                isMobile={isMobile}
                onMobileDataClick={() => {
                  setMobileDrawerOpen(false);
                }}
              />
            </div>
          )}

          {/* Floating card for animation - moved to parent container */}
          {!isMobile && floatingCard && (
            <div
              className="deck-card-wrapper floating-card"
              style={{
                position: 'fixed',
                zIndex: 9999,
                top: floatingCard.top,
                left: floatingCard.left,
                width: floatingCard.width,
                height: floatingCard.height,
                transition: floatingCard.animating ? 'all 0.5s cubic-bezier(.4,1.3,.6,1)' : 'none',
                transform: floatingCard.animating ? 'scale(1.08) translateY(0)' : 'none',
                pointerEvents: 'none'
              }}
              onTransitionEnd={handleFloatingCardTransitionEnd}
            >
              <SupportMemberTrumpCard
                member={searchFilteredMembers[floatingCard.idx]}
                monthlyTrendStats={data.staffMonthlyTrendStats}
                active={true}
                onClick={() => { }}
                onCloseActive={() => { }}
                isMobile={isMobile}
                onMobileDataClick={() => {
                  setMobileDrawerOpen(false);
                }}
              />
            </div>
          )}

          <div className="deck-of-cards-container">
            {searchFilteredMembers.length > 0 ? (
              searchFilteredMembers.map((member, idx) => (
                <React.Fragment key={member.name}>
                  {/* Regular cards - only show if not active */}
                  {activeCardIndex !== idx && (
                    <div
                      className="deck-card-wrapper"
                      style={{
                        zIndex: 100 + idx,
                        top: `${activeCardIndex !== null && idx > activeCardIndex ? ((idx - 1) * 48 + 48) + cardDeckTopOffset : (idx * 48) + cardDeckTopOffset}px`,
                        visibility: floatingCard && floatingCard.idx === idx ? 'hidden' : undefined
                      }}
                      ref={el => { cardDomRefs.current[idx] = el; }}
                      onClick={e => handleCardClick(e, idx, member)}
                    >
                      <SupportMemberTrumpCard
                        member={member}
                        monthlyTrendStats={data.staffMonthlyTrendStats}
                        active={false}
                        onClick={() => {
                          if (activeCardIndex !== idx) {
                            setActiveCardIndex(idx);
                            //setSelectedMember(member);
                          }
                        }}
                        onCloseActive={() => {
                          setActiveCardIndex(null);
                          setSelectedMember(null);
                        }}
                        isMobile={isMobile}
                        onMobileDataClick={() => {
                          setMobileDrawerOpen(false);
                        }}
                      />
                    </div>
                  )}

                  {/* Floating card removed from here - now in parent container */}
                </React.Fragment>
              ))
            ) : searchQuery && data.staffPerformance.length > 0 ? (
              <p style={{ padding: '20px', color: '#666' }}>
                No team members found matching "{searchQuery}".
              </p>
            ) : (
              <p style={{ padding: '20px', color: '#666' }}>
                No team member data available.
              </p>
            )}
          </div>
        </div>

        {/* Member Detail View or Team Detail View */}
        <div className="developer-details-section">
          {selectedMember ? (
            <SupportMemberDetail
              member={selectedMember}
              monthlyTrendStats={data.staffMonthlyTrendStats}
              snapshotStartDate={data.snapshotStartDate}
              snapshotEndDate={data.summaryDate}
            />
          ) : (
            <SupportTeamDetail
              staffPerformance={data.staffPerformance}
              monthlyTrendStats={data.staffMonthlyTrendStats}
              snapshotStartDate={data.snapshotStartDate}
              snapshotEndDate={data.summaryDate}
            />
          )}
        </div>
      </div>

      {/* AI Panel */}
      {data && (() => {
        const canGenerateAiSummary = (aiGeneratedDate: string | undefined): boolean => {
          return aiGeneratedDate && data?.summaryDate ? new Date(aiGeneratedDate) < new Date(data.summaryDate) : true;
        };

        return (
          <SlidingAiPanel
            insight={aiSummary?.summary || ''}          
            summaryCreatedOn={aiSummary?.createdOn || undefined}
            canGenerateNewSummary={canGenerateAiSummary(aiSummary?.createdOn || undefined)}
            contextType={selectedMember ? "support-team-member" : "support-team"}
            entityName={selectedMember ? selectedMember.name : 'Support Team'}
            entityId={selectedMember ? selectedMember.name : data.summaryId?.toString()}
            projectId={undefined}
            summaryId={data.summaryId?.toString()}
            initialOpen={false}
            shouldCloseOnOutisdeClick={false}
            buttonTitle={selectedMember ? "Team Member Exec Insights" : "Overview Exec Insights"}
            onStreamedContentChange={updateAiSummary}
            generateInitialAi={selectedMember ? fetchStaffSummary : undefined}
          />
        );
      })()}
    </div>
  );
};

export default SupportTeamMembers;
